import { z } from 'zod';
import { PLAYER_ID } from './ids';

export type ScenarioKey = 'base' | `out:${string}`;

const SCENARIO_KEY_PATTERN = new RegExp(`^(base|out:${PLAYER_ID}(,${PLAYER_ID})*)$`);
export const ScenarioKeySchema = z.string().regex(SCENARIO_KEY_PATTERN);

// Player ids distinct and numeric ascending: a scenario key's order.
const canonical = (ids: readonly string[]) => [...new Set(ids)].sort((a, b) => a.length - b.length || (a < b ? -1 : a > b ? 1 : 0));

// The canonical key for a set of out players: the form the canonical_scenario CHECK admits.
export function scenarioKey(outPlayerIds: readonly string[]): ScenarioKey {
  const ids = canonical(outPlayerIds);
  return ids.length === 0 ? 'base' : `out:${ids.join(',')}`;
}

export function parseScenarioKey(key: string): { outPlayerIds: string[] } {
  return { outPlayerIds: key === 'base' ? [] : key.slice(4).split(',') };
}

// The scenario a game prices at for a viewer (spec §3.2): its ruled-out players plus the chips (the questionable players the
// viewer may mark) he marked out. The board and its downloads choose it alike; a game that stores no such scenario is
// odds-only for him.
export function viewerScenario(ruledOut: readonly string[], chips: readonly string[], markedOut: (playerId: string) => boolean): { key: ScenarioKey; marked: string[] } {
  const marked = chips.filter(markedOut);
  return { key: scenarioKey([...ruledOut, ...marked]), marked };
}

// The scenario a player's analyst override carries from (effective.ts): his own team's out players alone. An override
// set there applies wherever only the opposing team's players sit besides; a teammate sitting changes his role, so it
// does not carry.
export const ownLineup = (scenario: string, teamOf: (playerId: string) => string | undefined, teamId: string): ScenarioKey =>
  scenarioKey(parseScenarioKey(scenario).outPlayerIds.filter(id => teamOf(id) === teamId));

// A questionable player's availability as the analyst rules it: out or in. Open (null) is priced in.
export const AvailabilitySchema = z.enum(['out', 'in']);
export type Availability = z.infer<typeof AvailabilitySchema>;

// Whether a game still offers a stored scenario: every player ruled out is out in it, and no player ruled in is.
export function offersScenario(scenario: string, ruled: ReadonlyMap<string, Availability>): boolean {
  const out = new Set(parseScenarioKey(scenario).outPlayerIds);
  return [...ruled].every(([id, a]) => (a === 'out') === out.has(id));
}
